// File: frontend/src/components/admin/AdminPins.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiPushpin2Fill, RiCloseLine } from "react-icons/ri";
import { adminPinService } from "../../services/pinService";
import { searchTopics, TopicOpsTarget, TopicOpsType } from "../../services/adminTopicOpsService";
import { AdminPinModel, PinTargetType } from "../../models/PinModel";
import { InputClearButton } from "../common/InputClearButton";
import "./AdminPins.css";

// Domain order convention: Gündem → Maç → Kulüp → Kişi → Takım
const TYPE_OPTIONS: { value: PinTargetType; labelKey: string; fallback: string }[] = [
  { value: "CUSTOM_TOPIC", labelKey: "admin_pins_type_custom", fallback: "Gündem" },
  { value: "MATCH",        labelKey: "admin_pins_type_match",  fallback: "Maç" },
  { value: "CLUB",         labelKey: "admin_pins_type_club",   fallback: "Kulüp" },
  { value: "PERSON",       labelKey: "admin_pins_type_person", fallback: "Kişi" },
  { value: "TEAM",         labelKey: "admin_pins_type_team",   fallback: "Takım" },
];

interface AdminPinsProps {
  isAdmin: boolean;
}

/**
 * Admin "Sabitlenenler" (pins) panel — ADMIN + MODERATOR.
 *
 * Top section: pin a new TOPIC — pick a domain, search by title (reuses the
 * topic-ops autocomplete endpoint) and pin it to the top of its left-pane list.
 * Bottom section: all current pin rows with unpin actions for both the topic
 * pin and the entry pin. Entry pinning itself is done in-place from the
 * 3-dot menu of any entry (visible to admins/mods on every topic page).
 */
// isAdmin is accepted for symmetry with the other admin components; pin management
// is available to both roles, so it is not read here (role checks are server-side).
export const AdminPins: React.FC<AdminPinsProps> = (_props: AdminPinsProps) => {
  const { t } = useTranslation();

  // Localized display label for a domain type (defined inside the component so
  // it closes over the correctly typed i18next t function)
  const typeLabel = (type: PinTargetType): string => {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? t(opt.labelKey, opt.fallback) : type;
  };

  const [pins, setPins] = useState<AdminPinModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New topic-pin form state
  const [searchType, setSearchType] = useState<PinTargetType>("CUSTOM_TOPIC");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TopicOpsTarget[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPins(await adminPinService.listPins());
    } catch (err) {
      console.error("Failed to load pins:", err);
      setError(t("admin_pins_load_error", "Sabitlenenler yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPins();
  }, [loadPins]);

  // Debounced title autocomplete (same endpoint as the move/merge panel)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await searchTopics(searchType as TopicOpsType, q));
      } catch (err) {
        console.error("Topic search failed:", err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchType]);

  const handlePinTopic = async (target: TopicOpsTarget) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminPinService.pinTopic(target.type as PinTargetType, target.id);
      setQuery("");
      setResults([]);
      await loadPins();
    } catch (err) {
      console.error("Pin topic failed:", err);
      setError(t("admin_pins_action_error", "İşlem başarısız oldu."));
    } finally {
      setBusy(false);
    }
  };

  const handleUnpinTopic = async (pin: AdminPinModel) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminPinService.unpinTopic(pin.targetType, pin.targetId);
      await loadPins();
    } catch (err) {
      console.error("Unpin topic failed:", err);
      setError(t("admin_pins_action_error", "İşlem başarısız oldu."));
    } finally {
      setBusy(false);
    }
  };

  const handleUnpinEntry = async (pin: AdminPinModel) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminPinService.unpinEntry(pin.targetType, pin.targetId);
      await loadPins();
    } catch (err) {
      console.error("Unpin entry failed:", err);
      setError(t("admin_pins_action_error", "İşlem başarısız oldu."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-pins">
      <h2 className="admin-pins__title">
        <RiPushpin2Fill /> {t("admin_pins_title", "Sabitlenenler")}
      </h2>
      <p className="admin-pins__hint">
        {t("admin_pins_hint",
          "Başlık sabitleme: başlık kendi listesinin en tepesinde gösterilir. Tanım sabitleme: ilgili başlık sayfasında tanımın üç nokta menüsünden yapılır ve tanım sayfanın en tepesinde gösterilir.")}
      </p>

      {error && <div className="admin-pins__error">{error}</div>}

      {/* ── Pin a new topic ── */}
      <div className="admin-pins__form">
        <select
          className="admin-pins__select"
          value={searchType}
          onChange={(e) => { setSearchType(e.target.value as PinTargetType); setResults([]); }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey, opt.fallback)}</option>
          ))}
        </select>
        <div className="admin-pins__search-wrap">
          <input
            className="admin-pins__search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("admin_pins_search_placeholder", "Sabitlenecek başlığı ara (en az 2 karakter)...")}
          />
          {/* Clear-X — also drops the results so the list does not keep showing
              matches for a query the admin has just wiped */}
          {query !== "" && (
            <InputClearButton onClear={() => { setQuery(""); setResults([]); }} />
          )}
        </div>
      </div>

      {searching && <div className="admin-pins__searching">{t("admin_pins_searching", "aranıyor...")}</div>}

      {results.length > 0 && (
        <div className="admin-pins__results">
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              className="admin-pins__result"
              onClick={() => handlePinTopic(r)}
              disabled={busy || r.merged}
              title={r.merged ? t("admin_pins_merged_hint", "Bu başlık birleştirilmiş, sabitlenemez.") : undefined}
            >
              <RiPushpin2Fill className="admin-pins__result-icon" />
              <span className="admin-pins__result-title">{r.title}</span>
              <span className="admin-pins__result-meta">
                {r.entryCount} {t("admin_pins_entry_count_suffix", "tanım")}
                {!r.active && ` · ${t("admin_pins_inactive", "pasif")}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Current pins ── */}
      <h3 className="admin-pins__subtitle">{t("admin_pins_current", "Mevcut sabitlemeler")}</h3>
      {loading ? (
        <div className="admin-pins__loading">{t("admin_pins_loading", "yükleniyor...")}</div>
      ) : pins.length === 0 ? (
        <div className="admin-pins__empty">{t("admin_pins_empty", "Henüz sabitlenmiş bir şey yok.")}</div>
      ) : (
        <table className="admin-pins__table">
          <thead>
            <tr>
              <th>{t("admin_pins_col_type", "Tip")}</th>
              <th>{t("admin_pins_col_title", "Başlık")}</th>
              <th>{t("admin_pins_col_topic_pin", "Başlık sabit")}</th>
              <th>{t("admin_pins_col_entry_pin", "Sabit tanım")}</th>
              <th>{t("admin_pins_col_by", "Sabitleyen")}</th>
            </tr>
          </thead>
          <tbody>
            {pins.map((pin) => (
              <tr key={pin.id}>
                <td>{typeLabel(pin.targetType)}</td>
                <td className="admin-pins__cell-title">{pin.title}</td>
                <td>
                  {pin.topicPinned ? (
                    <button
                      className="admin-pins__unpin-btn"
                      onClick={() => handleUnpinTopic(pin)}
                      disabled={busy}
                      title={t("admin_pins_unpin_topic", "Başlık sabitlemesini kaldır")}
                    >
                      <RiCloseLine /> {t("admin_pins_unpin", "kaldır")}
                    </button>
                  ) : (
                    <span className="admin-pins__none">—</span>
                  )}
                </td>
                <td>
                  {pin.pinnedEntryId != null ? (
                    <div className="admin-pins__entry-cell">
                      <span className="admin-pins__entry-snippet" title={pin.pinnedEntrySnippet ?? undefined}>
                        #{pin.pinnedEntryId}
                        {pin.pinnedEntryAuthor && ` · ${pin.pinnedEntryAuthor}`}
                      </span>
                      <button
                        className="admin-pins__unpin-btn"
                        onClick={() => handleUnpinEntry(pin)}
                        disabled={busy}
                        title={t("admin_pins_unpin_entry", "Tanım sabitlemesini kaldır")}
                      >
                        <RiCloseLine /> {t("admin_pins_unpin", "kaldır")}
                      </button>
                    </div>
                  ) : (
                    <span className="admin-pins__none">—</span>
                  )}
                </td>
                <td>{pin.pinnedByUsername ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminPins;